import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { observer } from 'mobx-react';
import { UitkButtonSize, UitkPrimaryButton } from 'uitk-react-button';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkHeading, UitkText } from 'uitk-react-text';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { useState } from 'react';
import { UitkIllustrations } from 'uitk-react-illustrations';
import { useStores } from '../../hooks';
import { analyticsEvent } from '../../util/analytics';
import { useLogger } from '@shared-ui/logger-context';
import { UnsubscribeEvents } from '../../observability/events/unsubscribe';

export interface UnsubscribeFormProps extends RouteComponentProps {
  email: string;
  unsubscribeAction: () => void;
}

const UnsubscribeRequestComponent: React.FunctionComponent<UnsubscribeFormProps> = observer(
  // tslint:disable-next-line:no-shadowed-variable
  function UnsubscribeFormComponent(props: UnsubscribeFormProps): JSX.Element {
    const LOGGER = useLogger();
    const { analytics, userAuth, pzStore } = useStores();
    const { email, unsubscribeAction } = props;
    const [inProgress, setInProgress] = useState(false);

    const rfrrIdRequestSelected = 'affhub_unsubscribe_request.selected';
    const rfrrIdRequestSucceeded = 'affhub_unsubscribe_request.succeeded';

    const analyticsTrackEventsMap = new Map([
      [rfrrIdRequestSelected, 'AffHub Unsubscribe Request Selected'],
      [rfrrIdRequestSucceeded, 'AffHub Unsubscribe Request Succeeded'],
    ]);
    const analyticsTrackEvents = (rfrr: string) => {
      analyticsEvent(analytics, userAuth, pzStore, rfrr, analyticsTrackEventsMap.get(rfrr));
    };

    const analyticsUnsubscribeRequested = {
      id: rfrrIdRequestSelected,
      callback: () => {
        analyticsTrackEvents(rfrrIdRequestSelected);
      },
    };

    const handleUnsubscribe = async () => {
      setInProgress(true);
      analyticsTrackEvents(rfrrIdRequestSucceeded);
      LOGGER.info(UnsubscribeEvents.ATTEMPTED);

      unsubscribeAction();
    };

    // TODO - localise text
    return (
      <UitkSpacing className="container--small-1" padding={{ block: 'twelve' }}>
        <main className="Unsubscribe__container">
          <UitkLayoutFlex direction="column" alignItems="start" justifyContent="space-between" wrap="wrap">
            <UitkLayoutFlexItem alignSelf="center">
              <UitkSpacing margin={{ block: 'three' }}>
                <UitkIllustrations alt="Mail" url="/images/icons/mail.svg" className="unsubscribeIcon" />
              </UitkSpacing>
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem alignSelf="center">
              <UitkSpacing margin={{ block: 'three' }}>
                <UitkHeading tag="h1" size={4}>
                  Need to unsubscribe?
                </UitkHeading>
              </UitkSpacing>
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem>
              <UitkSpacing margin={{ block: 'three' }}>
                <UitkText>
                  Unsubscribe from all marketing emails from the Expedia Group Affiliate Program. You will still receive
                  service emails and other important information.
                </UitkText>
              </UitkSpacing>
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem>
              <UitkSpacing margin={{ block: 'three' }}>
                <UitkText>Please confirm your email below.</UitkText>
              </UitkSpacing>
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem>
              <UitkSpacing margin={{ block: 'four', inline: 'two' }}>
                <div>
                  <UitkText size={300}>Email:</UitkText>
                  <UitkText>{email}</UitkText>
                </div>
              </UitkSpacing>
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem alignSelf="center">
              <UitkSpacing margin={{ block: 'three' }}>
                <UitkPrimaryButton
                  analytics={analyticsUnsubscribeRequested}
                  type="button"
                  size={UitkButtonSize.LARGE}
                  onClick={handleUnsubscribe}
                  disabled={inProgress}
                >
                  Unsubscribe
                </UitkPrimaryButton>
              </UitkSpacing>
            </UitkLayoutFlexItem>
          </UitkLayoutFlex>
        </main>
      </UitkSpacing>
    );
  },
);

export const UnsubscribeRequest = withRouter(UnsubscribeRequestComponent);
